export function AssemblyOverview({ mainConfig, moduleBConfig, assembly, catalog, mode = "finished" }) {
  // Ordre G/D configurable : a_left (défaut) ou b_left (inversé)
  const swapped = assembly?.module_order === "b_left";
  const configA = swapped ? moduleBConfig : mainConfig;
  const configB = swapped ? mainConfig : moduleBConfig;
  const configs = [configA, configB];
  const labels = swapped ? ["Module B", "Module A"] : ["Module A", "Module B"];
  const combinedWidth = configs.reduce((sum, config) => sum + config.total_width, 0);
  const maxHeight = Math.max(...configs.map((config) => config.height));
  const technical = mode === "technical";
  // En mode technique, réserver de l'espace pour les cotes (marges supplémentaires)
  const marginTop = technical ? 30 : 10;
  const marginLeft = technical ? 28 : 10;
  const drawAreaW = 320 - marginLeft - 10;
  const drawAreaH = 236 - marginTop - 50;
  const scale = Math.min(2.6, drawAreaW / combinedWidth, drawAreaH / maxHeight);
  const startX = marginLeft + (drawAreaW - combinedWidth * scale) / 2;
  const baseY = marginTop + maxHeight * scale;
  let cursor = startX;
  return (
    <svg data-testid="assembly-overview-2d" viewBox="0 0 320 236" className="w-full h-auto max-h-72" role="img" aria-label={technical ? "Vue technique atelier" : "Vue d'ensemble finie"}>
      <rect width="320" height="236" fill="#FDFBF7" />
      {configs.map((config, index) => {
        const moduleWidth = config.total_width * scale;
        const moduleHeight = config.height * scale;
        const top = baseY - moduleHeight;
        const origin = cursor;
        cursor += moduleWidth;

        const totalSectionCm = config.sections.reduce((s, sec) => s + (sec.width_cm || 0), 0) || config.total_width;
        const sectionWidths = config.sections.map((sec) => ((sec.width_cm || totalSectionCm / config.sections.length) / totalSectionCm) * moduleWidth);
        let sectionCursor = origin;

        const carcassFinish = catalog?.finishes?.find((f) => f.id === config.carcass_finish_id);
        const doorsFinish = catalog?.finishes?.find((f) => f.id === (config.doors_finish_id || config.carcass_finish_id));
        const fill = technical ? "#FDFBF7" : (carcassFinish?.swatch || (index === 0 ? "#C8A16B" : "#B8935A"));
        const doorFill = doorsFinish?.swatch || "#D8C09A";
        const pxPerMm = scale / 10;

        const moduleLabel = labels[index];
        const moduleDims = `${Math.round(config.total_width * 10)} × ${Math.round(config.height * 10)} mm`;
        return (
          <g key={index}>
            <rect x={origin} y={top} width={moduleWidth} height={moduleHeight} fill={fill} stroke="#374151" strokeWidth="1.5" />
            {config.sections.map((sec, si) => {
              const secLeft = sectionCursor;
              const secW = sectionWidths[si];
              sectionCursor += secW;
              const divider = si > 0 ? <line key={`div-${si}`} x1={secLeft} x2={secLeft} y1={top + 2} y2={baseY - 2} stroke="#374151" strokeWidth="1" /> : null;

              // Aménagements intérieurs — uniquement en mode technique
              const interiors = technical ? (sec.elements || []).map((el, ei) => {
                const elCenterY = baseY - el.y_mm * pxPerMm;
                const spanW = ((el.span_sections || 1) > 1 ? sectionWidths.slice(si, si + Math.min(el.span_sections, config.sections.length - si)).reduce((a, b) => a + b, 0) : secW);
                const elX = secLeft + 2;
                const maxRight = origin + moduleWidth;
                const elW = Math.min(spanW - 4, maxRight - elX - 2);
                if (elW <= 0) return null;

                if (el.kind === "shelf_fixed" || el.kind === "shelf_mobile") {
                  return (
                    <g key={`el-${si}-${ei}`}>
                      <line x1={elX} y1={elCenterY} x2={elX + elW} y2={elCenterY} stroke="#374151" strokeWidth={el.kind === "shelf_mobile" ? "0.7" : "1.2"} strokeDasharray={el.kind === "shelf_mobile" ? "3 2" : undefined} />
                      <text x={elX + elW + 2} y={elCenterY + 2.5} fontSize="5" fill="#0465C1" fontWeight="600">{Math.round(el.y_mm)}</text>
                    </g>
                  );
                }
                if (el.kind === "drawer") {
                  const dH = Math.max(8, (el.height_mm || 150) * pxPerMm);
                  const dTop = Math.max(top + 2, elCenterY - dH / 2);
                  const dBottom = Math.min(baseY - 2, dTop + dH);
                  const clampedH = dBottom - dTop;
                  if (clampedH < 3) return null;
                  return (
                    <g key={`el-${si}-${ei}`}>
                      <rect x={elX} y={dTop} width={elW} height={clampedH} rx="1" fill="rgba(0,0,0,0.08)" stroke="#374151" strokeWidth="0.8" />
                      <line x1={elX + elW * 0.3} y1={dTop + clampedH / 2} x2={elX + elW * 0.7} y2={dTop + clampedH / 2} stroke="#374151" strokeWidth="1" />
                      {clampedH > 10 && <text x={elX + elW / 2} y={dTop + clampedH / 2 - 3} textAnchor="middle" fontSize="5" fill="#374151">{Math.round(el.height_mm || 150)}</text>}
                      <text x={elX + elW + 2} y={dTop + clampedH / 2 + 2} fontSize="5" fill="#0465C1" fontWeight="600">{Math.round(el.y_mm)}</text>
                    </g>
                  );
                }
                if (el.kind === "penderie") {
                  return (
                    <g key={`el-${si}-${ei}`}>
                      <line x1={elX + 2} y1={elCenterY} x2={elX + elW - 2} y2={elCenterY} stroke="#374151" strokeWidth="1.5" strokeLinecap="round" />
                      <text x={elX + elW + 2} y={elCenterY + 2.5} fontSize="5" fill="#0465C1" fontWeight="600">{Math.round(el.y_mm)}</text>
                    </g>
                  );
                }
                return null;
              }) : null;

              // Cotes de largeur section (mode technique)
              const sectionDim = technical && secW > 20 ? (
                <g key={`dim-sec-${si}`}>
                  <line x1={secLeft + 2} y1={top - 4} x2={secLeft + secW - 2} y2={top - 4} stroke="#0465C1" strokeWidth="0.5" />
                  <text x={secLeft + secW / 2} y={top - 6} textAnchor="middle" fontSize="5.5" fill="#0465C1" fontWeight="600">{Math.round((sec.width_cm || config.total_width / config.sections.length) * 10)}</text>
                </g>
              ) : null;

              // Portes — uniquement en mode fini
              const door = (!technical && sec.has_door) ? (
                <rect key={`door-${si}`} x={secLeft + 3} y={top + 4} width={secW - 6} height={moduleHeight - 8} rx="2" fill={doorFill} stroke="#374151" strokeWidth="0.8" opacity="0.85" />
              ) : null;

              return <g key={`sec-${si}`}>{divider}{interiors}{sectionDim}{door}</g>;
            })}
            {/* Cote de hauteur (mode technique) — côté gauche du premier module */}
            {technical && index === 0 && (
              <g>
                <line x1={origin - 6} y1={top} x2={origin - 6} y2={baseY} stroke="#0465C1" strokeWidth="0.5" />
                <line x1={origin - 8} y1={top} x2={origin - 4} y2={top} stroke="#0465C1" strokeWidth="0.5" />
                <line x1={origin - 8} y1={baseY} x2={origin - 4} y2={baseY} stroke="#0465C1" strokeWidth="0.5" />
                <text x={origin - 10} y={top + moduleHeight / 2 + 2} textAnchor="middle" fontSize="5.5" fill="#0465C1" fontWeight="600" transform={`rotate(-90,${origin - 10},${top + moduleHeight / 2})`}>{Math.round(config.height * 10)} mm</text>
              </g>
            )}
            {/* Cote de largeur totale module (mode technique) */}
            {technical && (
              <g>
                <line x1={origin} y1={baseY + 6} x2={origin + moduleWidth} y2={baseY + 6} stroke="#0465C1" strokeWidth="0.5" />
                <text x={origin + moduleWidth / 2} y={baseY + 14} textAnchor="middle" fontSize="5.5" fill="#0465C1" fontWeight="600">{Math.round(config.total_width * 10)} mm</text>
              </g>
            )}
            <text x={origin + moduleWidth / 2} y={baseY + (technical ? 24 : 19)} textAnchor="middle" fontSize="10" fontWeight="700" fill="#172033">{moduleLabel}</text>
            {!technical && <text x={origin + moduleWidth / 2} y={baseY + 32} textAnchor="middle" fontSize="8" fill="#52606F">{moduleDims}</text>}
          </g>
        );
      })}
      {/* Jonction chantier */}
      <line x1={startX + configA.total_width * scale} x2={startX + configA.total_width * scale} y1={baseY - maxHeight * scale - (technical ? 2 : 9)} y2={baseY + 2} stroke="#0465C1" strokeDasharray="4 3" strokeWidth="1.5" />
      {assembly?.continuous_plinth_mm && <rect x={startX} y={baseY} width={combinedWidth * scale} height="4" fill="#7C6043" />}
    </svg>
  );
}
